import React, { useState } from "react";
import swal from "sweetalert";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Tooltip,
  Divider,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
} from "@mui/material";
import { getMemberHealthScore, getGroupHealthScores } from "../../services/reportService";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FilterBar, ReportHeader, currencyFmt } from "./ReportsPages";
import { useTranslation } from "react-i18next";

export function MemberHealthScoreReport() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({ from: "", to: "", group: "", member: "" });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    if (!filters.group) {
      swal(t("Missing Filter"), t("Please select a group to generate the report."), "warning");
      return;
    }

    setLoading(true);
    setError(null);
    setReportData(null);

    try {
      let data;
      if (filters.member) {
        // A specific member is selected, fetch their score
        data = await getMemberHealthScore(filters.member);
      } else {
        // Only a group is selected, fetch scores for all members
        data = await getGroupHealthScores(filters.group);
      }
      setReportData(data);
    } catch (err) {
      setError(err.message);
      swal(t("Error"), err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => swal(t("Info"), t("Export logic not yet implemented."), "info");

  const categoryColor = (cat) => {
    switch (cat) {
      case "Excellent":
        return "success";
      case "Good":
        return "info";
      case "Fair":
        return "warning";
      default:
        return "error";
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <ReportHeader
        title={t("Member Financial Health Score")}
        subtitle={t("Risk and reliability score per member based on repayments, arrears and penalties.")}
        roleHint={t("Access: Chairperson / Secretary / Accountant / Admin / Desk Officer")}
      />

      <FilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onGenerate={handleGenerate}
        isGenerating={loading}
        onExport={handleExport}
        showGroup showMember // Ensure both dropdowns are visible
      />

      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" fontWeight={800}>
                {t("Financial Health Overview")}
              </Typography>
              <Tooltip title={t("Score derived from repayment history, arrears, contribution consistency, penalties and defaults.")}>
                <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Stack>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          {loading && <Typography>{t("Loading report...")}</Typography>}
          {error && <Typography color="error">{t("Error")}: {error}</Typography>}

          {!reportData && !loading && (
            <Typography color="text.secondary" align="center" sx={{ my: 4 }}>
              {t("Select a group to see all members, or select a specific member, then click \"Generate\".")}
            </Typography>
          )}

          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("Member")}</TableCell>
                  <TableCell>{t("Group")}</TableCell>
                  <TableCell align="right">{t("Score")}</TableCell>
                  <TableCell>{t("Category")}</TableCell>
                  <TableCell align="right">{t("Arrears")}</TableCell>
                  <TableCell align="right">{t("Penalties")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData?.health_scores?.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.member_name}</TableCell>
                    <TableCell>{r.group_name}</TableCell>
                    <TableCell align="right">{r.score}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t(r.category)}
                        color={categoryColor(r.category)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {currencyFmt(r.arrears)} TZS
                    </TableCell>
                    <TableCell align="right">
                      {currencyFmt(r.penalties)} TZS
                    </TableCell>
                  </TableRow>
                ))}
                {reportData && reportData?.health_scores?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        {t("No health score data available for the selected member.")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </CardContent>
      </Card>
    </Box>
  );
}
